import { useState, useEffect } from 'react';

/**
 * Storage keys prefix to avoid collisions
 */
const STORAGE_PREFIX = 'chatealo_';

/**
 * Get storage key with prefix and optional restaurant scope
 */
function getStorageKey(key: string, restaurantId?: string): string {
  if (restaurantId) {
    return `${STORAGE_PREFIX}${restaurantId}_${key}`;
  }
  return `${STORAGE_PREFIX}${key}`;
}

/**
 * Load value from localStorage
 */
function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as T;
    }
  } catch {
    // Ignore storage errors
  }
  return defaultValue;
}

/**
 * Save value to localStorage
 */
function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (e.g., quota exceeded)
  }
}

/**
 * Hook for persisted state using localStorage
 * State is automatically saved when it changes and restored on mount
 *
 * @param key - Storage key (will be prefixed)
 * @param defaultValue - Default value if nothing is stored
 * @param restaurantId - Optional restaurant ID to scope the storage
 * @returns [value, setValue] tuple like useState
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  restaurantId?: string
): [T, (value: T | ((prev: T) => T)) => void] {
  const storageKey = getStorageKey(key, restaurantId);

  // Initialize state from localStorage
  const [value, setValue] = useState<T>(() => loadFromStorage(storageKey, defaultValue));

  // Save to localStorage whenever value changes
  useEffect(() => {
    saveToStorage(storageKey, value);
  }, [storageKey, value]);

  return [value, setValue];
}

/**
 * Interface for the app navigation state that should be persisted
 */
export interface PersistedNavState {
  view: 'menu' | 'cart' | 'checkout' | 'product' | 'history';
  activeCategoryId: string;
  scrollPosition: number;
  selectedProductId?: string;
}

/**
 * Default navigation state
 */
export const DEFAULT_NAV_STATE: PersistedNavState = {
  view: 'menu',
  activeCategoryId: '',
  scrollPosition: 0,
};

/**
 * Save navigation state to localStorage
 * This is separate from the hook for use in event handlers
 */
export function saveNavState(state: PersistedNavState, restaurantId: string): void {
  const key = getStorageKey('nav_state', restaurantId);
  saveToStorage(key, state);
}

/**
 * Load navigation state from localStorage
 */
export function loadNavState(restaurantId: string): PersistedNavState {
  const key = getStorageKey('nav_state', restaurantId);
  return loadFromStorage(key, DEFAULT_NAV_STATE);
}

/**
 * Clear navigation state (e.g., after order completion)
 */
export function clearNavState(restaurantId: string): void {
  const key = getStorageKey('nav_state', restaurantId);
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Interface for the app mode state (admin/client)
 */
export interface PersistedAppMode {
  mode: 'admin' | 'client';
  timestamp: number;
}

/**
 * Save app mode to localStorage
 */
export function saveAppMode(mode: 'admin' | 'client', restaurantId: string): void {
  const key = getStorageKey('app_mode', restaurantId);
  saveToStorage(key, { mode, timestamp: Date.now() });
}

/**
 * Load app mode from localStorage
 * Returns null if no stored mode or if it's older than maxAge (default 1 hour)
 */
export function loadAppMode(restaurantId: string, maxAgeMs = 3600000): 'admin' | 'client' | null {
  const key = getStorageKey('app_mode', restaurantId);
  const stored = loadFromStorage<PersistedAppMode | null>(key, null);

  if (stored && Date.now() - stored.timestamp < maxAgeMs) {
    return stored.mode;
  }
  return null;
}

